import { BroadviewClientError } from "@broadview/api-client";
import { z } from "zod";
import type { PortalProblemRecordReader } from "./portal-runtime";

const portalProblemRecordSchema = z
  .object({
    id: z.string().trim().min(1),
    tenantId: z.string().trim().min(1),
    displayId: z.string().trim().min(1),
    title: z.string().trim().min(1),
    summary: z.string(),
    severity: z.enum(["UNASSESSED", "LOW", "MEDIUM", "HIGH", "CRITICAL"]),
    status: z.enum(["DRAFT", "OPEN", "ON_HOLD", "CLOSED"]),
    ownerUserId: z.string().trim().min(1).nullable(),
    version: z.number().int().nonnegative(),
    createdAt: z.string().datetime({ offset: true }),
    createdBy: z.string().trim().min(1),
    updatedAt: z.string().datetime({ offset: true }),
    updatedBy: z.string().trim().min(1),
    classification: z.enum(["HISTORICAL_REAL", "OPERATIONAL"]).optional(),
    claimCount: z.number().int().nonnegative().optional(),
    impact: z
      .object({
        amountMinor: z.string().regex(/^-?\d+$/),
        currencyCode: z.string().regex(/^[A-Z]{3}$/),
      })
      .strict()
      .optional(),
    dataCutoffAt: z.string().datetime({ offset: true }).optional(),
    freshness: z.enum(["CURRENT", "STALE", "UNKNOWN"]).optional(),
    quality: z.enum(["VERIFIED", "PARTIAL"]).optional(),
    reconciliation: z.enum(["PASSED", "EXCEPTION", "UNKNOWN"]).optional(),
    sourceSummary: z.string().trim().min(1).max(1_000).optional(),
  })
  .strict();

const problemRecordListSchema = z.object({ items: z.array(portalProblemRecordSchema) }).strict();

function apiError(status: number, correlationId?: string): BroadviewClientError {
  const code =
    status === 401
      ? "UNAUTHENTICATED"
      : status === 403
        ? "FORBIDDEN"
        : status === 404
          ? "NOT_FOUND"
          : "UNAVAILABLE";
  return new BroadviewClientError({
    status,
    code,
    correlationId,
    retryable: status === 429 || status >= 500,
    message:
      status === 403
        ? "You do not have permission to read Problem Records for this Division."
        : status === 404
          ? "The requested Problem Record was not found in this Division."
          : "Problem Records are temporarily unavailable.",
  });
}

function normalizeOrigin(apiOrigin: string): string {
  let url: URL;
  try {
    url = new URL(apiOrigin);
  } catch {
    throw new BroadviewClientError({
      status: 503,
      code: "LIVE_ADAPTER_DISABLED",
      message: "Problem Record reads require a valid HTTPS API origin.",
    });
  }
  if (url.protocol !== "https:" || url.pathname !== "/" || url.search || url.hash) {
    throw new BroadviewClientError({
      status: 503,
      code: "LIVE_ADAPTER_DISABLED",
      message: "Problem Record reads require an HTTPS API origin without a path.",
    });
  }
  return url.origin;
}

export function createLiveProblemRecordReader(
  options: Readonly<{
    apiOrigin: string;
    getAccessToken: () => Promise<string | undefined>;
    fetchImpl?: typeof fetch;
  }>,
): PortalProblemRecordReader {
  const apiOrigin = normalizeOrigin(options.apiOrigin);
  const fetchImpl = options.fetchImpl ?? globalThis.fetch;

  async function read(path: string, tenantId: string): Promise<unknown> {
    const token = await options.getAccessToken();
    if (!token) {
      throw new BroadviewClientError({
        status: 401,
        code: "UNAUTHENTICATED",
        message: "A verified Broadview session is required.",
      });
    }
    let response: Response;
    try {
      response = await fetchImpl(`${apiOrigin}/api/v1${path}`, {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "X-Correlation-Id": crypto.randomUUID(),
          "X-Tenant-Id": tenantId,
        },
      });
    } catch {
      throw new BroadviewClientError({
        status: 503,
        code: "UNAVAILABLE",
        retryable: true,
        message: "Problem Records are temporarily unavailable. Fixture data was not substituted.",
      });
    }
    const correlationId = response.headers.get("x-correlation-id") ?? undefined;
    if (!response.ok) throw apiError(response.status, correlationId);
    try {
      return await response.json();
    } catch {
      throw new BroadviewClientError({
        status: 502,
        code: "CONTRACT_MISMATCH",
        correlationId,
        message: "The Problem Record response was not valid JSON.",
      });
    }
  }

  function requireTenant<T extends { tenantId: string }>(value: T, tenantId: string): T {
    if (value.tenantId !== tenantId) {
      throw new BroadviewClientError({
        status: 502,
        code: "CONTRACT_MISMATCH",
        message: "The Problem Record response did not match the selected Division.",
      });
    }
    return value;
  }

  return {
    async list(tenantId) {
      const payload = await read("/problem-records", tenantId);
      const parsed = problemRecordListSchema.safeParse(payload);
      if (!parsed.success) {
        throw new BroadviewClientError({
          status: 502,
          code: "CONTRACT_MISMATCH",
          message: "The Problem Record list did not match the approved portal contract.",
        });
      }
      return parsed.data.items.map((record) => requireTenant(record, tenantId));
    },
    async get(tenantId, id) {
      const payload = await read(`/problem-records/${encodeURIComponent(id)}`, tenantId);
      const parsed = portalProblemRecordSchema.safeParse(payload);
      if (!parsed.success) {
        throw new BroadviewClientError({
          status: 502,
          code: "CONTRACT_MISMATCH",
          message: "The Problem Record detail did not match the approved portal contract.",
        });
      }
      return requireTenant(parsed.data, tenantId);
    },
  };
}
